<template>
    <page-loading v-if="loading"></page-loading>
    <form-container v-else>
        <step-form-heading v-if="!props.groupCollectionId"
            :icon="faRectangleHistoryCirclePlus">
            New Group Collection

            <template v-slot:description>
                Create a reusable filter for Groups.
            </template>
        </step-form-heading>

        <page-header v-else
            class="mb-6 lg:mb-12">
            <template v-slot:subtitle>
                <a :href="collectionsHref"
                    class="no-underline! text-sky-600! text-base font-normal">
                    <font-awesome-icon widthAuto class="mr-1"
                        :icon="faArrowLeft"></font-awesome-icon>
                    All Collections
                </a>
            </template>

            {{ groupCollection.name }}
        </page-header>

        <div :class="{ 'grid grid-cols-12': props.groupCollectionId }">
            <page-sidebar v-if="props.groupCollectionId"
                appearance="nested"
                :links="[
                    props.orgUnitId
                    ? { href: `/settings/teams/${teamId}/org-units/${props.orgUnitId}/group-collections/${props.groupCollectionId}`, label: 'Details' }
                    : { href: `/settings/teams/${teamId}/group-collections/${props.groupCollectionId}`, label: 'Details' },
                ]"></page-sidebar>
            <form-wrapper
                :sections="[
                    { id: 'name', label: 'Name' },
                    { id: 'description', label: 'Description' },
                    { id: 'filters', label: 'Groups' },
                ]"
                :hideNav="Boolean(props.groupCollectionId)"
            >
                <vx-alert v-if="onCorporate() && props.orgUnitId"
                    color="info"
                    :closeable="false"
                    shadow>
                    <template v-slot:icon>
                        <font-awesome-icon widthAuto :icon="faSlidersSimple"></font-awesome-icon>
                    </template>
                    <div class="flex items-center justify-between">
                        <span data-test="org-unit-state"><strong>{{ props.groupCollectionId ? 'In OU:' : 'Creating in' }}</strong> {{ orgUnit?.name || 'OU' }}</span>
                        <vx-button :href="collectionsHref"
                            size="sm"
                            data-test="cancel"
                            color="info">{{ props.groupCollectionId ? 'Back to Collections' : 'Cancel' }}<font-awesome-icon widthAuto :icon="faSquareArrowUpLeft"></font-awesome-icon></vx-button>
                    </div>
                </vx-alert>

                <vx-expandable-card id="name">
                    <template v-slot:header>
                        Name
                    </template>
                    <template v-slot:content>
                        <vx-input data-test="name"
                            autofocus
                            v-model="groupCollection.name"
                            size="lg"
                            :error="displayFirstError(validation.name.$errors)"></vx-input>
                    </template>
                </vx-expandable-card>

                <vx-expandable-card id="description">
                    <template v-slot:header>
                        Description
                    </template>
                    <template v-slot:content>
                        <vx-input data-test="description"
                            v-model="groupCollection.description"
                            size="lg"
                            :rows="4"
                            :error="displayFirstError(validation.description.$errors)"></vx-input>
                    </template>
                </vx-expandable-card>

                <vx-expandable-card id="filters">
                    <template v-slot:header>
                        Group Filters
                    </template>

                    <template v-slot:content>
                        <group-collection-filters
                            v-model="groupCollection.group_filters"
                            :orgUnitId="props.orgUnitId"
                        ></group-collection-filters>
                    </template>
                </vx-expandable-card>

                <vx-sticky-footer>
                    <vx-button :href="collectionsHref"
                        data-test="footer-cancel"
                        class="w-20"
                        size="lg"
                        color="muted">
                        Cancel
                    </vx-button>
                    <vx-button @click="submit()"
                        data-test="submit-button"
                        class="grow"
                        size="lg"
                        color="primary"
                        :loading="processing">
                        {{
                            props.groupCollectionId
                                ? 'Update'
                                : 'Create'
                        }} Group Collection
                    </vx-button>
                </vx-sticky-footer>

            </form-wrapper>
        </div>
    </form-container>
</template>

<script setup>
import StepFormHeading from '~/components/general/StepFormHeading.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageSidebar from '~/components/general/PageSidebar.vue';
import FormContainer from '~/components/general/FormContainer.vue';
import { VxButton, VxAlert, VxStickyFooter, VxExpandableCard, VxInput } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faSquareArrowUpLeft, faArrowLeft, faRectangleHistoryCirclePlus, faSlidersSimple } from '@fortawesome/pro-solid-svg-icons';
import { ref, onMounted } from 'vue';
import { required, maxLength } from '@vuelidate/validators';
import { alphanumericDashUnderscoreSpace, displayFirstError } from '~/utils/validation';
import { groupCollectionsClient, orgUnitsClient } from '~/services';
import GroupCollectionFilters from './GroupCollectionFilters.vue';
import { randomString } from '~/utils/string';
import FormWrapper from '~/components/general/FormWrapper.vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useFormSubmission } from '~/composables/useFormSubmission.js';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';

const teamId = Spark.state.currentTeam.id;
const { onCorporate } = useCurrentGroup();

const props = defineProps({
    orgUnitId: {
        type: Number,
    },
    groupCollectionId: {
        type: [String, Number],
        required: false,
    },
});

const orgUnit = ref();

const collectionsHref = onCorporate() && props.orgUnitId
    ? `/settings/teams/${teamId}/org-units/${props.orgUnitId}/group-collections`
    : `/settings/teams/${teamId}#/${props.orgUnitId ? 'current-org-unit-collections' : 'group-collections'}`;

const defaultData = () => {
    return {
        name: '',
        group_filters: [
            {
                id: randomString(),
                attribute: '',
                operator: '',
                key: '',
                values: [],
            }
        ],
    }
}
const groupCollection = ref(defaultData());
const loading = ref(false);
const toasted = useToasted();

onMounted(async () => {
    if (onCorporate() && props.orgUnitId) {
        loading.value = true;
        try {
            const orgUnitResponse = await orgUnitsClient.showOrgUnit(teamId, props.orgUnitId)
            orgUnit.value = orgUnitResponse.data;
            loading.value = false;
        } catch (e) {
            toasted.error(catchRequestError(e))
        }
    }

    if (props.groupCollectionId) {
        loading.value = true;
        try {
            const response = await groupCollectionsClient.getGroupCollection({ teamId, orgUnitId: props.orgUnitId }, props.groupCollectionId);
            groupCollection.value = response.data;
            loading.value = false;
        } catch (e) {
            toasted.error(catchRequestError(e));
        }
    }
});

const save = async () => {
    if (!props.groupCollectionId) {
        await groupCollectionsClient.createGroupCollection({ teamId, orgUnitId: props.orgUnitId }, groupCollection.value);
        groupCollection.value = defaultData();
    } else {
        await groupCollectionsClient.updateGroupCollection({ teamId, orgUnitId: props.orgUnitId }, props.groupCollectionId, groupCollection.value);
    }
};

const { submit, processing, validation, } = useFormSubmission({
    data: groupCollection,
    validation: {
        name: {
            required,
            maxLength: maxLength(80),
            format: alphanumericDashUnderscoreSpace,
        },
        description: {
            maxLength: maxLength(2000)
        }
    },
    preserveScroll: () => Boolean(props.groupCollectionId),
    submit: save,
    reset: () => {
        if (!props.groupCollectionId) {
            groupCollection.value = defaultData();
        }
    },
    notification: () => ({
        message: `Group Collection ${props.groupCollectionId ? 'updated' : 'created'}`,
        action: {
            icon: faSquareArrowUpLeft,
            label: 'Back to Collections',
            href: collectionsHref,
        },
    }),
});

</script>
